import React, { useState, useEffect, useRef } from 'react'
import { useEditorStore } from '../store.js'
import { examplePrompts, ExamplePromptKey } from '../../examples/prompts.js'

export function EditorNavBar() {
  const [showProjectSelector, setShowProjectSelector] = useState(false)
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [showExamplesMenu, setShowExamplesMenu] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [projectToDelete, setProjectToDelete] = useState<{ id: string; name: string } | null>(null)

  const projectSelectorRef = useRef<HTMLDivElement>(null)
  const examplesMenuRef = useRef<HTMLDivElement>(null)

  const projects = useEditorStore(state => state.projects)
  const currentProject = useEditorStore(state => state.currentProject)
  const createProject = useEditorStore(state => state.createProject)
  const loadProject = useEditorStore(state => state.loadProject)
  const deleteProject = useEditorStore(state => state.deleteProject)
  const theme = useEditorStore(state => state.theme)
  const setTheme = useEditorStore(state => state.setTheme)
  const viewMode = useEditorStore(state => state.viewMode)
  const setViewMode = useEditorStore(state => state.setViewMode)
  const zoom = useEditorStore(state => state.zoom)
  const setZoom = useEditorStore(state => state.setZoom)
  const setChatInput = useEditorStore(state => state.setChatInput)
  const setRightSidebarTab = useEditorStore(state => state.setRightSidebarTab)

  const handleNewProject = () => {
    if (newProjectName.trim()) {
      createProject(newProjectName.trim())
      setNewProjectName('')
      setShowNewProjectDialog(false)
    }
  }

  const handleLoadExample = (key: ExamplePromptKey) => {
    setChatInput(examplePrompts[key])
    setRightSidebarTab('chat')
    setShowExamplesMenu(false)
  }

  const handleDeleteProject = () => {
    if (currentProject) {
      setProjectToDelete({ id: currentProject.id, name: currentProject.name })
    }
  }

  const confirmDelete = () => {
    if (projectToDelete) {
      deleteProject(projectToDelete.id)
      setProjectToDelete(null)
      setShowProjectSelector(false)
    }
  }

  const showToast = (message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 3000)
  }

  const handleScreenshot = async () => {
    const canvas = document.getElementById('wireframelm-canvas-root')
    if (!canvas) return

    try {
      const { default: html2canvas } = await import('html2canvas')
      const canvasEl = await html2canvas(canvas, {
        backgroundColor: '#ffffff',
        scale: 2
      })

      canvasEl.toBlob(blob => {
        if (blob) {
          const item = new ClipboardItem({ 'image/png': blob })
          navigator.clipboard.write([item]).then(() => {
            showToast('Screenshot copied to clipboard')
          }).catch(err => {
            console.error('Failed to copy screenshot:', err)
            // Fallback: download the image
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${currentProject?.name || 'wireframe'}-screenshot.png`
            a.click()
            URL.revokeObjectURL(url)
            showToast('Screenshot downloaded')
          })
        }
      })
    } catch (error) {
      console.error('Failed to capture screenshot:', error)
      showToast('Failed to capture screenshot')
    }
  }

  const exampleEntries = Object.entries(examplePrompts) as [ExamplePromptKey, string][]

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectSelectorRef.current && !projectSelectorRef.current.contains(event.target as Node)) {
        setShowProjectSelector(false)
      }
      if (examplesMenuRef.current && !examplesMenuRef.current.contains(event.target as Node)) {
        setShowExamplesMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <nav className="editor-navbar">
      <div className="editor-navbar__left">
        <h1 className="editor-navbar__logo">WireframeLM</h1>

        <div className="editor-navbar__project-controls">
          <div className="editor-navbar__dropdown-wrapper" ref={projectSelectorRef}>
            <button
              type="button"
              className="editor-navbar__project-button"
              onClick={() => setShowProjectSelector(!showProjectSelector)}
            >
              {currentProject ? currentProject.name : 'Select Project'}
              <span className="editor-navbar__dropdown-icon">▾</span>
            </button>
            {showProjectSelector && (
              <div className="editor-navbar__dropdown">
                {projects.length === 0 ? (
                  <div className="editor-navbar__dropdown-empty">No projects yet</div>
                ) : (
                  projects.map(project => (
                    <div key={project.id} className="editor-navbar__dropdown-item-wrapper">
                      <button
                        type="button"
                        className="editor-navbar__dropdown-item"
                        onClick={() => {
                          loadProject(project.id)
                          setShowProjectSelector(false)
                        }}
                      >
                        {project.name}
                      </button>
                      <button
                        type="button"
                        className="editor-navbar__dropdown-delete"
                        onClick={(e) => {
                          e.stopPropagation()
                          setProjectToDelete({ id: project.id, name: project.name })
                        }}
                        title="Delete project"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="editor-navbar__action-button"
            onClick={() => setShowNewProjectDialog(true)}
          >
            New
          </button>

          {currentProject && (
            <button type="button" className="editor-navbar__action-button editor-navbar__action-button--primary" onClick={handleScreenshot}>
              <span className="material-symbols-outlined text-base">photo_camera</span>
              Capture
            </button>
          )}
        </div>
      </div>

      <div className="editor-navbar__right">
        <div className="mode-toggle">
          <button
            type="button"
            className={viewMode === 'edit' ? 'mode-toggle__button is-active' : 'mode-toggle__button'}
            onClick={() => setViewMode('edit')}
          >
            Edit
          </button>
          <button
            type="button"
            className={viewMode === 'interactive' ? 'mode-toggle__button is-active' : 'mode-toggle__button'}
            onClick={() => setViewMode('interactive')}
          >
            Interactive
          </button>
        </div>
        <div className="flex items-center gap-1 px-2">
          <button
            type="button"
            className="editor-navbar__icon-button"
            onClick={() => setZoom(zoom - 10)}
            title="Zoom out"
          >
            <span className="material-symbols-outlined text-base">remove</span>
          </button>
          <button
            type="button"
            className="editor-navbar__action-button min-w-[4rem] px-2"
            onClick={() => setZoom(100)}
            title="Reset zoom"
          >
            {Math.round(zoom)}%
          </button>
          <button
            type="button"
            className="editor-navbar__icon-button"
            onClick={() => setZoom(zoom + 10)}
            title="Zoom in"
          >
            <span className="material-symbols-outlined text-base">add</span>
          </button>
        </div>
        <button
          type="button"
          className="editor-navbar__action-button"
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
        >
          <span className="material-symbols-outlined text-base">{theme === 'light' ? 'dark_mode' : 'light_mode'}</span>
          {theme === 'light' ? 'Dark' : 'Light'}
        </button>
        <div className="editor-navbar__dropdown-wrapper" ref={examplesMenuRef}>
          <button
            type="button"
            className="editor-navbar__action-button"
            onClick={() => setShowExamplesMenu(!showExamplesMenu)}
          >
            Examples
          </button>
          {showExamplesMenu && (
            <div className="editor-navbar__dropdown">
              {exampleEntries.map(([key]) => (
                <button
                  key={key}
                  type="button"
                  className="editor-navbar__dropdown-item"
                  onClick={() => handleLoadExample(key)}
                >
                  {key}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showNewProjectDialog && (
        <div className="editor-dialog-overlay" onClick={() => setShowNewProjectDialog(false)}>
          <div className="editor-dialog" onClick={e => e.stopPropagation()}>
            <h3 className="editor-dialog__title">New Project</h3>
            <input
              type="text"
              className="editor-dialog__input"
              placeholder="Project name"
              value={newProjectName}
              onChange={e => setNewProjectName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleNewProject()}
              autoFocus
            />
            <div className="editor-dialog__actions">
              <button type="button" className="editor-dialog__button" onClick={() => setShowNewProjectDialog(false)}>
                Cancel
              </button>
              <button type="button" className="editor-dialog__button editor-dialog__button--primary" onClick={handleNewProject}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {projectToDelete && (
        <div className="editor-dialog-overlay" onClick={() => setProjectToDelete(null)}>
          <div className="editor-dialog" onClick={e => e.stopPropagation()}>
            <h3 className="editor-dialog__title">Delete Project</h3>
            <p className="text-sm text-slate-600">
              Are you sure you want to delete "{projectToDelete.name}"? This action cannot be undone.
            </p>
            <div className="editor-dialog__actions">
              <button type="button" className="editor-dialog__button" onClick={() => setProjectToDelete(null)}>
                Cancel
              </button>
              <button type="button" className="editor-dialog__button bg-red-600 text-white border-red-600 hover:bg-red-700" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed top-14 left-1/2 -translate-x-1/2 z-50 rounded-lg bg-slate-800 px-4 py-3 text-sm text-white shadow-lg animate-fade-in">
          {toast}
        </div>
      )}
    </nav>
  )
}
